import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";

const SanaNoteForm = ({ formData, setFormData, handleChange }) => {
    const [rows, setRows] = useState([]);

    // Initialize rows from formData if available
    useEffect(() => {
        setRows(
            formData.progressNotes.length > 0
                ? formData.progressNotes
                : [
                    {
                        id: Date.now(),
                        dateTime: "",
                        pulse: "",
                        bp: "",
                        rr: "",
                        temp: "",
                        weight: "",
                        bsf: "",
                        bsr: "",
                        physicianNote: "",
                        nurseName: "",

                        // ✅ New fields
                        height: "",
                        bmi: "",
                        abdCirc: "",
                        chestCirc: "",
                        hba1c: "",
                        urea: "",
                        crt: "",
                        lipidPro: "",
                    },
                ]
        );
    }, [formData.progressNotes]);

    // Add a new progress note row
    const addRow = () => {
        setRows([
            ...rows,
            {
                id: Date.now(),
                dateTime: "",
                pulse: "",
                bp: "",
                rr: "",
                temp: "",
                weight: "",
                bsf: "",
                bsr: "",
                physicianNote: "",
                nurseName: "",

                // ✅ New fields
                height: "",
                bmi: "",
                abdCirc: "",
                chestCirc: "",
                hba1c: "",
                urea: "",
                crt: "",
                lipidPro: "",
            },
        ]);
    };

    // Remove a row
    const removeRow = (id) => {
        if (rows.length === 1) return;
        Swal.fire({
            title: "Delete this entry?",
            text: "This will remove the selected progress note.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete it",
        }).then((res) => {
            if (res.isConfirmed) {
                const updatedRows = rows.filter((r) => r.id !== id);
                setRows(updatedRows);
                setFormData({ ...formData, progressNotes: updatedRows }); // sync deletion
            }
        });
    };

    // Handle row changes
    const handleRowChange = (id, field, value) => {
        const updatedRows = rows.map((r) =>
            r.id === id ? { ...r, [field]: value } : r
        );
        setRows(updatedRows);
        setFormData({ ...formData, progressNotes: updatedRows });
    };

    return (
        <div className="p-4">
            {/* ==== CLIENT NAME ==== */}
            <div className="row mb-4">
                <div className="col-md-4">
                    <div className="form-floating form-floating-outline">
                        <input
                            type="text"
                            name="clientName"
                            className="form-control"
                            placeholder="Client Name"
                            value={formData.clientName || ""}
                            onChange={handleChange}
                            readOnly={!!formData.patientId || !!formData.patient_id}
                        />
                        <label>Client Name</label>
                    </div>
                </div>
            </div>

            {/* ==== PROGRESS NOTES ROWS ==== */}
            <div className="col-12 mb-4">
                <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3 px-3 py-1">
                    Progress Notes (Physician)
                </small>
            </div>

            {rows.map((row) => (
                <div
                    key={row.id}
                    className="border rounded-4 p-3 mb-3 shadow-sm position-relative bg-light-subtle"
                >
                    <div className="row g-3 align-items-center">
                        <div className="col-md-4">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="datetime-local"
                                    className="form-control"
                                    value={row.dateTime}
                                    onChange={(e) =>
                                        handleRowChange(row.id, "dateTime", e.target.value)
                                    }
                                />
                                <label>Date & Time</label>
                            </div>
                        </div>

                        <div className="col-md-2">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Physician Note"
                                    value={row.physicianNote}
                                    onChange={(e) =>
                                        handleRowChange(row.id, "physicianNote", e.target.value)
                                    }
                                />
                                <label>Physician Note</label>
                            </div>
                        </div>

                        <div className="col-md-2">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Nurse Name"
                                    value={row.nurseName}
                                    onChange={(e) =>
                                        handleRowChange(row.id, "nurseName", e.target.value)
                                    }
                                />
                                <label>Nurse Name</label>
                            </div>
                        </div>

                        <div className="col-md-1">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="number"
                                    className="form-control"
                                    placeholder="Pulse"
                                    value={row.pulse}
                                    onChange={(e) =>
                                        handleRowChange(row.id, "pulse", e.target.value)
                                    }
                                />
                                <label>Pulse</label>
                            </div>
                        </div>

                        <div className="col-md-1">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="B.P."
                                    value={row.bp}
                                    onChange={(e) =>
                                        handleRowChange(row.id, "bp", e.target.value)
                                    }
                                />
                                <label>B.P.</label>
                            </div>
                        </div>

                        <div className="col-md-1">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="number"
                                    className="form-control"
                                    placeholder="R/R"
                                    value={row.rr}
                                    onChange={(e) =>
                                        handleRowChange(row.id, "rr", e.target.value)
                                    }
                                />
                                <label>R/R</label>
                            </div>
                        </div>

                        <div className="col-md-1">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="number"
                                    className="form-control"
                                    placeholder="Temp."
                                    value={row.temp}
                                    onChange={(e) =>
                                        handleRowChange(row.id, "temp", e.target.value)
                                    }
                                />
                                <label>Temp.</label>
                            </div>
                        </div>

                        <div className="col-md-1">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="number"
                                    className="form-control"
                                    placeholder="Weight"
                                    value={row.weight}
                                    onChange={(e) =>
                                        handleRowChange(row.id, "weight", e.target.value)
                                    }
                                />
                                <label>Weight</label>
                            </div>
                        </div>

                        <div className="col-md-1">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="number"
                                    className="form-control"
                                    placeholder="Height"
                                    value={row.height}
                                    onChange={(e) =>
                                        handleRowChange(row.id, "height", e.target.value)
                                    }
                                />
                                <label>Height</label>
                            </div>
                        </div>

                        <div className="col-md-1">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="number"
                                    className="form-control"
                                    placeholder="BMI"
                                    value={row.bmi}
                                    onChange={(e) =>
                                        handleRowChange(row.id, "bmi", e.target.value)
                                    }
                                />
                                <label>BMI</label>
                            </div>
                        </div>

                        <div className="col-md-1">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="number"
                                    className="form-control"
                                    placeholder="BSF"
                                    value={row.bsf}
                                    onChange={(e) =>
                                        handleRowChange(row.id, "bsf", e.target.value)
                                    }
                                />
                                <label>BSF</label>
                            </div>
                        </div>

                        <div className="col-md-1">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="number"
                                    className="form-control"
                                    placeholder="BSR"
                                    value={row.bsr}
                                    onChange={(e) =>
                                        handleRowChange(row.id, "bsr", e.target.value)
                                    }
                                />
                                <label>BSR</label>
                            </div>
                        </div>

                        <div className="col-md-1">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="number"
                                    className="form-control"
                                    placeholder="Abd Circ"
                                    value={row.abdCirc}
                                    onChange={(e) =>
                                        handleRowChange(row.id, "abdCirc", e.target.value)
                                    }
                                />
                                <label>Abd Circ</label>
                            </div>
                        </div>

                        <div className="col-md-1">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="number"
                                    className="form-control"
                                    placeholder="Chest Circ"
                                    value={row.chestCirc}
                                    onChange={(e) =>
                                        handleRowChange(row.id, "chestCirc", e.target.value)
                                    }
                                />
                                <label>Chest Circ</label>
                            </div>
                        </div>

                        <div className="col-md-1">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="number"
                                    className="form-control"
                                    placeholder="HbA1c"
                                    value={row.hba1c}
                                    onChange={(e) =>
                                        handleRowChange(row.id, "hba1c", e.target.value)
                                    }
                                />
                                <label>HbA1c</label>
                            </div>
                        </div>

                        <div className="col-md-1">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="number"
                                    className="form-control"
                                    placeholder="Urea"
                                    value={row.urea}
                                    onChange={(e) =>
                                        handleRowChange(row.id, "urea", e.target.value)
                                    }
                                />
                                <label>Urea</label>
                            </div>
                        </div>

                        <div className="col-md-1">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="number"
                                    className="form-control"
                                    placeholder="Crt"
                                    value={row.crt}
                                    onChange={(e) =>
                                        handleRowChange(row.id, "crt", e.target.value)
                                    }
                                />
                                <label>Crt</label>
                            </div>
                        </div>

                        <div className="col-md-1">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="number"
                                    className="form-control"
                                    placeholder="Lipid Pro"
                                    value={row.lipidPro}
                                    onChange={(e) =>
                                        handleRowChange(row.id, "lipidPro", e.target.value)
                                    }
                                />
                                <label>Lipid Pro</label>
                            </div>
                        </div>

                        <div className="col-md-1 mt-3">
                            <button
                                type="button"
                                className="btn btn-lg btn-outline-danger"
                                onClick={() => removeRow(row.id)}
                            >
                                <i className="ri-delete-bin-line"></i>
                            </button>
                        </div>
                    </div>
                </div>
            ))}

            <div className="text-end mt-3">
                <button type="button" className="btn btn-outline-primary" onClick={addRow}>
                    <i className="ri-add-line"></i> Add Row
                </button>
            </div>

            {/* ==== DOCUMENT INFORMATION ==== */}
            <div className="col-12 mt-5 mb-3">
                <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3 px-3 py-1">
                    Document Information
                </small>
            </div>

            <div className="row g-3 mb-4">
                <div className="col-md-4">
                    <div className="form-floating form-floating-outline">
                        <input
                            type="text"
                            name="documentName"
                            className="form-control"
                            value="Progress Notes (Physician)"
                            readOnly
                        />
                        <label>Document Name</label>
                    </div>
                </div>

                <div className="col-md-4">
                    <div className="form-floating form-floating-outline">
                        <input
                            type="date"
                            name="effectiveDate"
                            className="form-control"
                            value={formData.effectiveDate || ""}
                            onChange={handleChange}
                        />
                        <label>Effective Date</label>
                    </div>
                </div>

                <div className="col-md-4">
                    <div className="form-floating form-floating-outline">
                        <input
                            type="text"
                            name="approvedBy"
                            className="form-control"
                            placeholder="Approved By"
                            value={formData.approvedBy || ""}
                            onChange={handleChange}
                        />
                        <label>Approved By</label>
                    </div>
                </div>

                <div className="col-md-4">
                    <div className="form-floating form-floating-outline">
                        <input
                            type="date"
                            name="approvedDate"
                            className="form-control"
                            value={formData.approvedDate || ""}
                            onChange={handleChange}
                        />
                        <label>Approved Date</label>
                    </div>
                </div>

                <div className="col-md-4">
                    <div className="form-floating form-floating-outline">
                        <input
                            type="text"
                            name="nextRevisionBy"
                            className="form-control"
                            placeholder="Next Revision By"
                            value={formData.nextRevisionBy || ""}
                            onChange={handleChange}
                        />
                        <label>Next Revision By</label>
                    </div>
                </div>

                <div className="col-md-4">
                    <div className="form-floating form-floating-outline">
                        <input
                            type="date"
                            name="nextRevisionDate"
                            className="form-control"
                            value={formData.nextRevisionDate || ""}
                            onChange={handleChange}
                        />
                        <label>Next Revision Date</label>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SanaNoteForm;
